import React, { useEffect } from 'react';
import { Box, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useInternalMessageByIdLazyQuery } from '../../api/internalMessageById.graphql.generated';
import { deriveProviderLabel } from '../../MessagesV2.utils';

type OriginalMessageSubrowProps = Readonly<{
  /** The `originalMessageId` from the resend row — the original's own uuid, not a provider id. */
  originalMessageId: string;
}>;

/**
 * Shows the message a resend was copied from, inside the row's existing expanded panel.
 *
 * Fetched on first expand rather than prefetched with the table: most rows are never opened, and
 * the list query stays cheap. Apollo's cache handles collapse/re-expand, so reopening the same row
 * does not refetch.
 *
 * A null result renders "original not found" rather than an error. `internalMessageById` returns
 * null for an unknown id by design, so a dangling reference is an expected state — the original
 * may have been deleted — and must not look like a crash.
 */
export const OriginalMessageSubrow: React.FC<OriginalMessageSubrowProps> = ({ originalMessageId }) => {
  const { tq } = useTranslation('messagesV2ServiceCenter');
  const [fetchOriginal, { data, loading, error, called }] = useInternalMessageByIdLazyQuery();

  useEffect(() => {
    fetchOriginal({ variables: { id: originalMessageId } });
  }, [fetchOriginal, originalMessageId]);

  const original = data?.internalMessageById;

  return (
    <Box
      marginTop={14}
      paddingTop={12}
      borderTop="1px solid #E5E7EB"
    >
      <TextV2
        fontSize="12.5px"
        fontWeight={700}
        marginBottom="8px"
      >
        {tq`Original message`}
      </TextV2>

      {(loading || !called) && (
        <TextV2
          typographyVariant="label3"
          fontSize="11px"
          color="mono10"
        >
          {tq`Loading the original…`}
        </TextV2>
      )}

      {!loading && (error || (called && !original)) && (
        <TextV2
          typographyVariant="label3"
          fontSize="11px"
          color="mono10"
        >
          {tq`Original not found — it may have been deleted.`}
        </TextV2>
      )}

      {!loading && original && (
        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fit, minmax(160px, 1fr))"
          gap="8px 20px"
        >
          <Field
            label={tq`Sent`}
            value={new Date(original.createAt.milliseconds).toLocaleString()}
          />
          <Field
            label={tq`Recipient`}
            value={original.recipient.email || original.recipient.phoneNumber || '–'}
          />
          <Field
            label={tq`Status`}
            value={original.rawStatus}
          />
          <Field
            label={tq`Message type`}
            value={original.messageType || '–'}
          />
          {/* A resend copies the original's provider rather than choosing fresh, so reading this
              against the resend row's own Provider column is the point of showing it here. Blanked
              on email by the same rule the table uses — see `deriveProviderLabel`. */}
          <Field
            label={tq`Provider`}
            value={deriveProviderLabel(original) || '–'}
          />
          <Field
            label={tq`Provider id`}
            value={original.messageId || '–'}
          />
          <Field
            label={tq`From`}
            value={original.fromEmail || '–'}
          />
        </Box>
      )}
    </Box>
  );
};

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box style={{ minWidth: 0 }}>
    <TextV2
      typographyVariant="label3"
      fontSize="10px"
      color="mono10"
    >
      {label}
    </TextV2>
    <TextV2
      typographyVariant="label3"
      fontSize="11.5px"
      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {value}
    </TextV2>
  </Box>
);
